import { SubmitInput } from "components/Form/SubmitInput";
import { Space } from "components/SpaceBooking/types";
import { useThrottle } from "hooks";
import { useApi } from "../../../../hooks/useApi";
import useAuth from "hooks/useAuth";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import { isEqual, pick } from "lodash";
import { ComponentProps, useEffect, useRef, useState } from "react";
import { useFormContext, useFormState } from "react-hook-form";
import styled from "styled-components";
import { useClubMemberAuthStore } from "../../../ClubMemberManagement/ClubMember/ClubMemberAuthorization";

const StyledPriceView = styled.div<{ hasError: boolean }>`
    display: flex;
    justify-content: center;
    padding: 0.5rem;
    text-align: center;
    margin-bottom: 1rem;
    color: ${({ theme }) => theme.colors.white};

    background-color: ${({ hasError, theme }) => (hasError ? theme.colors.red : theme.colors.secondary)};
    border-radius: ${({ theme }) => theme.borderRadius};
`;

interface Props extends Pick<ComponentProps<typeof SubmitInput>, "children" | "isLoading"> {
    price: number;
    setPrice: (newPrice: number) => void;
    email?: string;
    spaceSubscriptionId?: number;
}

export const SubmitWithPriceCalculator = ({
                                              price,
                                              setPrice,
                                              email: _email,
                                              spaceSubscriptionId,
                                              children,
                                              isLoading: _isLoading
                                          }: Props) => {
    const { watch } = useFormContext();
    const api = useApi();
    const data = watch();
    const { isDirty } = useFormState();
    const { isAuthenticated } = useAuth();
    const [error, setError] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [lastValues, setLastValues] = useState(null);
    const email = _email ?? watch("email");
    const lastEmail = useRef<string>(email);
    const debouncedEmail = useDebouncedValue(email, 500);
    const isChecking = isAuthenticated || debouncedEmail;
    const { clubMemberAuthToken } = useClubMemberAuthStore();

    const check = () => {
        if (isChecking) {
            setIsLoading(true);
            api({
                url: spaceSubscriptionId ? `space/booking/check/${spaceSubscriptionId}` : "space/booking/check",
                method: "POST",
                data: {
                    ...data,
                    email,
                    clubMemberAuthToken,
                    spaces: data.spaces?.map((space: Space) => space.id)
                },
                onBefore: () => setIsLoading(false),
                onSuccess: ({ price }) => {
                    setError(null);
                    setPrice(price);
                },
                muted: true
            }).then((error) => {
                setError(error.response?.data?.message);
            });
        }
    };

    const throttledCheck = useThrottle(check, 1000);

    useEffect(() => {
        check();
    }, []);

    const fields = ["spaces", "start_time", "end_time", "start", "weekday", "repeat_period", "repeat_until", "fellows", "exemptions", "clubMemberAuthToken"];
    useEffect(() => {
        if (
            lastEmail.current !== debouncedEmail ||
            (!isEqual(pick({ ...data, clubMemberAuthToken }, fields), pick(lastValues, fields)))
        ) {
            setLastValues({ ...data, clubMemberAuthToken });
            lastEmail.current = debouncedEmail;
            if (
                data.spaces?.length > 0 &&
                data.start_time &&
                data.end_time &&
                (data.start || data.weekday) &&
                (!data.repeat_period || (data.repeat_period && data.repeat_until))
            ) {
                throttledCheck();
            } else {
                setPrice(null);
            }
        }
    }, [isDirty, lastValues, data, debouncedEmail, clubMemberAuthToken]);

    return (
        <>
            <StyledPriceView hasError={error !== null}>
                {!isChecking
                    ? "Bitte gebe zuerst deine E-Mail Adresse vollständig an"
                    : error || (price !== null ? price.toLocaleString("de", { minimumFractionDigits: 2 }) : "-") + " €"}
            </StyledPriceView>
            <SubmitInput
                block
                disabled={!isChecking || error !== null}
                isLoading={_isLoading || (isChecking && isLoading)}
            >
                {children || "Weiter"}
            </SubmitInput>
        </>
    );
};
